import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Grid, Card, Button, Empty, Toast, PullToRefresh, Dialog, Checkbox
} from 'antd-mobile'
import './index.css'
import {
  UnorderedListOutline, AddOutline, FileOutline, HistogramOutline,
  StarOutline, HeartOutline, ClockCircleOutline
} from 'antd-mobile-icons'
import { useNavigate } from 'react-router-dom'
import API_ROUTES from '../api'
import axios from 'axios'
import GameEventList from '../Utils/GameEventList'
import CloseInfo from './closeInfo'
import LogoShow from './logoShow'
import Icon from '../icon'
import GameYear from './gameYear'
import { useAuth } from '../app/providers/AuthProvider'

const Home = () => {

  const navigate = useNavigate();
  const { memberInfo, isGuest, isAuthenticated } = useAuth()

  const [gameList, setGameList] = useState([])
  const [closeGameList, setCloseGameList] = useState([])
  const [mvpDataInfo, setMvpDataInfo] = useState({ checking: true })
  const mvpReminderShownRef = useRef(null)
  const [latestGameLoading, setLatestGameLoading] = useState(false)
  const [upcomingDinner, setUpcomingDinner] = useState(null)

  const getGameInfo = useCallback(() => {
    return axios.get(API_ROUTES.game_list, {
      params: {
        count: 1,
        status: "3",
        current: 1,
        size: 1
      }
    }, null)
      .then(function (response) {
        setGameList(response.data.data.records)
        Toast.clear()
      })
      .catch(function (error) {
        Toast.show({ icon: 'fail', content: '查询失败!' })
      })
      .finally(() => { Toast.clear() });
  }, [])

  const getCloseGameInfo = useCallback(() => {
    return axios.get(API_ROUTES.game_list, {
      params: {
        count: 10,
        status: memberInfo?.admin === 1 ? '0,1,2,5' : '0,1,2',
        current: 1,
        size: 10,
        memberId: memberInfo?.id
      }
    }, null)
      .then(function (response) {
        const resultData = response.data.data.records
        setCloseGameList(resultData)
        Toast.clear()
      })
      .catch(function (error) {
        Toast.show({ icon: 'fail', content: '查询失败!' })
      })
      .finally(() => { Toast.clear() });
  }, [memberInfo?.admin, memberInfo?.id])

  const getMvpVoteGameId = (mvpData) => {
    if (typeof mvpData?.clickInfo === 'string') return mvpData.clickInfo;
    return mvpData?.gameId || mvpData?.game?.id || mvpData?.clickInfo?.gameId || mvpData?.clickInfo?.id;
  }

  const navigateToMvpVote = useCallback((mvpData) => {
    const targetGameId = getMvpVoteGameId(mvpData);
    if (targetGameId) {
      navigate(`/games/${targetGameId}/history`);
      return;
    }
    Toast.show({ icon: 'fail', content: '暂未找到上一场内讧赛' })
  }, [navigate])

  const showMvpReminder = useCallback((mvpData) => {
    const dismissedTime = localStorage.getItem('mvpDialogDismissed');
    const now = new Date().getTime();
    // 上一场未参赛者也收到自愿投票提醒，但只有参赛者报名下一场内讧赛时受限。
    if ((dismissedTime && parseInt(dismissedTime) > now) || mvpData?.likeMemberId || !mvpData?.gameId || memberInfo?.temp === '1'
      || mvpReminderShownRef.current === mvpData.gameId) {
      return;
    }
    mvpReminderShownRef.current = mvpData.gameId

    let checked = false
    Dialog.confirm({
      className: 'mvp-reminder-modal',
      content: (
        <div className="mvp-dialog-body">
          <div className="mvp-reminder-icon">🏆</div>
          <div className="mvp-reminder-title">
            还没投票？
          </div>
          <div style={{ color: '#4E5969', fontSize: '14px', padding: '0 10px', lineHeight: '1.4' }}>
            {mvpData?.clickInfo
              ? '你参加了上一场内讧赛，完成 MVP 投票后才能报名下一场内讧赛。'
              : '上一场内讧赛 MVP 正在评选，欢迎参与投票；本场报名不受影响。'}
          </div>
          <div className="mvp-dialog-checkbox-wrapper">
            <Checkbox
              style={{ '--size': '16px' }}
              onChange={(value) => { checked = value }}
            />
            <span className="mvp-dialog-checkbox-text">今日不再提示</span>
          </div>
        </div>
      ),
      onConfirm: () => {
        if (checked) {
          const today = new Date();
          const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1, 0, 0, 0);
          localStorage.setItem('mvpDialogDismissed', endOfDay.getTime().toString());
        }
        navigateToMvpVote(mvpData)
      },
    })
  }, [memberInfo?.temp, navigateToMvpVote])

  const getMvpStatusInfo = useCallback(() => {
    if (memberInfo?.temp === '1') return Promise.resolve();

    return axios.get(API_ROUTES.game_mvpStatus, {
      params: {
        memberId: memberInfo?.id,
      }
    }, null)
      .then(function (response) {
        if (response.data?.code !== 200) throw new Error(response.data?.msg || '查询失败')
        const mvpData = response.data.data || {}
        setMvpDataInfo({ ...mvpData, checking: false })
        Toast.clear()
      })
      .catch(function () {
        setMvpDataInfo({ checking: true, error: true })
        Toast.show({ icon: 'fail', content: 'MVP 投票状态查询失败，请下拉刷新重试' })
      })
      .finally(() => { Toast.clear() });
  }, [memberInfo?.id, memberInfo?.temp])

  useEffect(() => {
    // 仅在有内讧赛开放报名时展示上一场内讧赛 MVP 提醒，其他比赛不受打扰。
    if (closeGameList.some(game => Number(game.gameType) === 3 && Number(game.status) === 0)
      && !mvpDataInfo?.checking) {
      showMvpReminder(mvpDataInfo)
    }
  }, [closeGameList, mvpDataInfo, showMvpReminder])

  const getTodayText = useCallback(() => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
  }, [])

  const getDinnerDateText = useCallback((item) => {
    return item?.dinnerDate || item?.matchTime?.slice?.(0, 10) || item?.createTime?.slice?.(0, 10) || '';
  }, [])

  const getUpcomingDinnerInfo = useCallback(() => {
    return axios.get(API_ROUTES.dinner_list, {
      params: { current: 1, size: 500 }
    }).then((response) => {
      const records = response.data?.data?.records || [];
      const today = getTodayText();
      // 首页只提示今天及之后仍有效的聚餐，避免过期聚餐打扰用户。
      const validDinner = (Array.isArray(records) ? records : [])
        .filter(item => Number(item.status) !== 2)
        .filter(item => {
          const dinnerDate = getDinnerDateText(item);
          return dinnerDate && dinnerDate >= today;
        })
        .sort((a, b) => getDinnerDateText(a).localeCompare(getDinnerDateText(b)))[0] || null;
      setUpcomingDinner(validDinner);
    }).catch(() => {
      setUpcomingDinner(null);
    });
  }, [getDinnerDateText, getTodayText])

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    getMvpStatusInfo()
    getCloseGameInfo()
    getGameInfo()
    getUpcomingDinnerInfo()
    localStorage.setItem('navigation', []);
  }, [getCloseGameInfo, getGameInfo, getMvpStatusInfo, getUpcomingDinnerInfo, isAuthenticated, navigate]);

  const handleLatestGameQuery = (e) => {
    e?.stopPropagation();
    setLatestGameLoading(true);
    getGameInfo().finally(() => {
      setLatestGameLoading(false);
    });
  }

  const refreshLatestGameIfQueried = () => {
    // 首页最近一场比赛数据恢复为自动加载，下拉刷新也同步刷新这块数据。
    return getGameInfo();
  }

  const goLatestGameDetail = () => {
    const gid = gameList[0]?.id;
    const status = gameList[0]?.status;
    if (gid) {
      if (status === 0) navigate(`/games/${gid}/signup`);
      else if (status === 1) navigate(`/games/${gid}/ready`);
      else if (status === 2) navigate(`/games/${gid}/live`);
      else if (status === 3) navigate(`/games/${gid}/history`);
    }
  }

  return (
    <div className="home-container ui-page">
      <PullToRefresh onRefresh={async () => { await refreshLatestGameIfQueried(); await getCloseGameInfo(); await getMvpStatusInfo(); await getUpcomingDinnerInfo(); }}>
        <div className="home-header-banner">
          <div className="home-header-logo-container">
            <img src={Icon.FIFTEEN_TH} alt="Club Logo" className="home-header-logo" />
          </div>
          <div className="home-header-subtitle">科朋足球 · 赛程与数据中心</div>
        </div>

        <GameYear />

        {upcomingDinner && (
          <section
            className="home-dinner-reminder"
            onClick={() => navigate(`/dinners/${upcomingDinner.id}`)}
          >
            <div className="home-dinner-reminder-icon">🍽️</div>
            <div className="home-dinner-reminder-main">
              <span>近期聚餐提醒</span>
              <strong>{upcomingDinner.position || upcomingDinner.remark || '聚餐安排'}</strong>
              <p>{getDinnerDateText(upcomingDinner)}{upcomingDinner.remark ? ` · ${upcomingDinner.remark}` : ''}</p>
            </div>
            <div className="home-dinner-reminder-action">查看</div>
          </section>
        )}

        <Card
          className="home-recent-matches-card ui-card"
          title={
            <div className="home-card-title">
              <span>🏆 最近赛事</span>
            </div>
          }
          extra={<span className="home-card-extra">点击名称查看详情</span>}
        >
          {closeGameList && closeGameList.length > 0 ?
            closeGameList.map((closeInfo, index) => {
              const getStatusClass = (status) => {
                if (status === 0) return 'status--pending';
                if (status === 1) return 'status--confirmed';
                if (status === 2) return 'status--live';
                if (status === 3) return 'status--finished';
                return '';
              };

              return (
                <div key={index} className={`home-recent-match-item ${getStatusClass(closeInfo.status)}`}>
                  <CloseInfo
                    gameDetail={closeInfo}
                    mvpDataInfo={mvpDataInfo}
                    getCloseGameInfo={getCloseGameInfo}
                    getGameInfo={refreshLatestGameIfQueried}
                  />
                </div>
              )
            }) : <Empty description="暂无近期赛事" imageStyle={{ width: 64 }} />
          }
        </Card>

        <Card
          className='home-latest-game-card ui-card'
          onClick={goLatestGameDetail}
          title={
            <div className="home-card-title">
              <UnorderedListOutline className="text-accent-blue" />
              <span>最近一场比赛数据</span>
            </div>
          }
          extra={
            <Button
              size='mini'
              loading={latestGameLoading}
              className='home-latest-query-button'
              onClick={handleLatestGameQuery}
            >
              刷新
            </Button>
          }
        >
          {gameList && gameList.length > 0 ? (
            <GameEventList gameInfo={gameList[0]} />
          ) : <Empty description='暂无数据' imageStyle={{ width: 64 }} />}
          <LogoShow />
        </Card>

        {/* 访客保留公开浏览入口：所有比赛与精彩瞬间，其余需要身份的功能继续隐藏。 */}
        <Card
          title={
            <div className="home-card-title">
              <UnorderedListOutline className="text-accent-orange" />
              <span>常用功能</span>
            </div>
          }
          className='home-card home-function-buttons ui-card'
        >
          <Grid columns={2} gap={12}>
            {!isGuest && memberInfo && memberInfo.admin === 1 && (
              <Grid.Item >
                <Button block className='home-function-button home-btn-primary' onClick={(e) => { e.stopPropagation(); navigate('/gameCreate') }}>
                  <AddOutline /> 创建比赛
                </Button>
              </Grid.Item>
            )}
            <Grid.Item>
              <Button block className='home-function-button home-btn-primary' onClick={(e) => { e.stopPropagation(); navigate('/gameHistory') }}>
                <FileOutline /> 所有比赛
              </Button>
            </Grid.Item>
            {!isGuest && (
              <Grid.Item>
                <Button block className='home-function-button home-btn-stats' onClick={(e) => { e.stopPropagation(); navigate('/confrontation') }}>
                  <HistogramOutline /> 带队统计
                </Button>
              </Grid.Item>
            )}
            <Grid.Item>
              <Button block className='home-function-button home-btn-album' onClick={(e) => { e.stopPropagation(); navigate('/album') }}>
                <span className="home-function-emoji">📷</span> 精彩瞬间
              </Button>
            </Grid.Item>
            {!isGuest && (
              <>
                <Grid.Item>
                  <Button block className='home-function-button home-btn-event' onClick={(e) => { e.stopPropagation(); navigate('/gameYearList') }}>
                    <StarOutline /> 年会活动
                  </Button>
                </Grid.Item>
                <Grid.Item>
                  <Button block className='home-function-button home-btn-social' onClick={(e) => { e.stopPropagation(); navigate('/happyEvent') }}>
                    <HeartOutline /> 科朋喜事
                  </Button>
                </Grid.Item>
                <Grid.Item>
                  <Button block className='home-function-button home-btn-social' onClick={(e) => { e.stopPropagation(); navigate('/dinnerList') }}>
                    <ClockCircleOutline /> 聚餐记录
                  </Button>
                </Grid.Item>
              </>
            )}
          </Grid>
        </Card>
      </PullToRefresh >
    </div>
  )
}

export default Home
